import { useTranslation } from "@sagot/i18n";
import { Badge, cn, cssVars, Icon, type BadgeTone, type IconName } from "@sagot/ui";
import { channelEntries, reasonKey, trendHeights, type GapReasonKey } from "./gap-model";

const REASON_STYLE: Readonly<Record<GapReasonKey, { tone: BadgeTone; icon: IconName }>> = {
  no_source: { tone: "danger", icon: "x-circle" },
  outdated: { tone: "warning", icon: "history" },
  low_confidence: { tone: "warning", icon: "gauge" },
  thumbs_down: { tone: "danger", icon: "thumbs-down" },
};

/** Reason = icon + text + colour (rule 10). */
export function ReasonBadge({ reason }: { reason: string | null }) {
  const { t } = useTranslation();
  const key = reasonKey(reason);
  const style = REASON_STYLE[key];
  return (
    <Badge tone={style.tone}>
      <Icon name={style.icon} className="size-3" />
      {t(`engage.gaps.reason.${key}`)}
    </Badge>
  );
}

const CHANNEL_ICON: Readonly<Record<string, IconName>> = {
  widget: "globe",
  webapp: "app-window",
  mobile: "smartphone",
  messenger: "message-circle",
  email: "mail",
  api: "code",
};

export function ChannelCounts({ counts }: { counts: Readonly<Record<string, number>> }) {
  const { t } = useTranslation();
  return channelEntries(counts).map(([channel, count]) => (
    <span key={channel} className="inline-flex items-center gap-1">
      <Icon name={CHANNEL_ICON[channel] ?? "message-square"} className="size-3" />
      {t(`engage.channels.${channel}`, { defaultValue: channel })} {count}
    </span>
  ));
}

const BAR_TONE = (height: number) => (height === 0 ? "bg-line-strong" : height >= 80 ? "bg-brand-500" : height >= 50 ? "bg-brand-300" : "bg-brand-200");

/** Ask count + 7-day mini bars (exposed as one image with a text summary). */
export function AskCount({ count, trend }: { count: number; trend: readonly number[] }) {
  const { t } = useTranslation();
  const heights = trendHeights(trend);
  return (
    <div>
      <div className="text-lg font-extrabold tabular-nums">
        {count} <span className="text-xs font-semibold text-fg-subtle">{t("engage.gaps.asks", { count })}</span>
      </div>
      {heights ? (
        <div role="img" aria-label={`${t("engage.gaps.trendLabel")}: ${trend.join(", ")}`} className="mt-1 flex h-4 items-end gap-0.5">
          {heights.map((height, index) => (
            <span
              // Bars are positional (day index); the series never reorders.
              key={index}
              className={cn("h-[var(--geo-h)] w-1.5", BAR_TONE(height))}
              style={cssVars({ "--geo-h": `${String(Math.max(height, 8))}%` })}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
